// Round-sync events: what the funder actually waits for.
//
// The server half of a save is already traced (Sentry's
// PrismaStorageAdapter::performAction), but that stops at the server boundary.
// It cannot tell us how long the person waited for their edit to be confirmed,
// and — the reason this exists — it cannot be sliced by where they were sitting.
// PostHog stamps geography onto every event, so these are the numbers that
// answer "is a funder reviewing from Estonia getting acceptable write latency?"
//
// The engine applies edits optimistically, so confirmation lag is invisible in
// the UI until it gets bad enough to trip the 20s stall guard. Before this, we
// measured only that catastrophe — never the p50.

import { type AnalyticsProperties, captureClientEvent } from "./analyticsEvent";

export const ROUND_SYNC_EVENTS = {
  actionConfirmed: "round action confirmed",
  roundOpened: "round opened",
  /**
   * An edit was accepted locally and NOT put on the wire, because the device is
   * offline. Emitted from the TRANSPORT (ImperativeState's `runEmitWithRetry`),
   * which is the only layer that knows — an earlier version keyed it off the
   * engine client's own `offlineMode`, which no production caller sets, so the
   * event fired only in tests.
   */
  editQueuedOffline: "round edit queued offline",
  /** A round opened with edits still waiting after reconciliation. */
  offlineEditsPending: "round offline edits pending",
} as const;

export function captureRoundSyncEvent({
  event,
  properties,
}: {
  event: (typeof ROUND_SYNC_EVENTS)[keyof typeof ROUND_SYNC_EVENTS];
  properties: AnalyticsProperties;
}): void {
  captureClientEvent({ event, sourceFamily: "round_sync", properties });
}
